import { createElement, lazy, type ComponentType } from 'react';

const REACT_LAZY = Symbol.for('react.lazy');

/**
 * Route pages loaded on first use: each page module becomes its own chunk (maps, charts, flow builders and the like stay
 * out of the first download and out of one giant bundle). `const { A, B } = lazyPages(() => import('./x'))` gives lazy
 * components for the module's named exports; the module is fetched once for all of them. Rendered inside <Suspense>.
 * An export that is itself a React.lazy component (e.g. src/nms) is rendered through a plain wrapper — a lazy
 * component must resolve to a function or class, not to another lazy one.
 */
export function lazyPages<T extends Record<string, unknown>>(load: () => Promise<T>): { [K in keyof T]: T[K] extends ComponentType<infer P> ? ComponentType<P> : never } {
  let mod: Promise<T> | null = null;
  const get = () => (mod ??= load());
  return new Proxy({}, {
    get: (_t, name) => lazy(async (): Promise<{ default: ComponentType<any> }> => {
      const exp = (await get())[name as keyof T] as unknown as ComponentType<any> & { $$typeof?: symbol };
      if (exp && exp.$$typeof === REACT_LAZY) return { default: (props: any) => createElement(exp, props) };
      return { default: exp };
    }),
  }) as any;
}
